/**
 * Cache-bust helper for asset URLs that live outside the webpack
 * bundle (currently the FROST WASM modules in `/orchard-frost/`,
 * `/frozt/`, and `/fromt-frost/`).
 *
 * Why we need this: the WASM `.wasm` + glue `.js` files are served
 * straight from `public/` and webpack never fingerprints them. When
 * we ship a new build the browser happily keeps serving the old
 * cached bytes, which manifests as `orchard-frost-wasm and
 * frozt-wasm both missing` (the new helper symbols are absent from
 * the cached module). Appending `?v=<token>` forces the browser to
 * revalidate the response whenever the token changes.
 *
 * Token strategy (highest priority first):
 *   1. `REACT_APP_BUILD_ID`  — set by CI per build (preferred).
 *   2. `REACT_APP_GIT_SHA`   — alternative CI-supplied identifier.
 *   3. `REACT_APP_VERSION`   — package version pinned at build.
 *   4. `process.env.npm_package_version` — populated by CRA from
 *      package.json at build time.
 *   5. Per-session timestamp — guarantees a fresh fetch on each load
 *      in dev so iterating on WASM changes doesn't require manual
 *      cache clears, and acts as a last-ditch fallback in prod.
 */

const sessionToken = (() => {
	try {
		return Date.now().toString(36);
	} catch {
		return 'dev';
	}
})();

let cachedToken = null;

const computeCacheToken = () => {
	if (cachedToken) return cachedToken;
	const env = (typeof process !== 'undefined' && process.env) || {};
	const candidates = [
		env.REACT_APP_BUILD_ID,
		env.REACT_APP_GIT_SHA,
		env.REACT_APP_VERSION,
		env.npm_package_version,
	];
	for (const c of candidates) {
		if (typeof c === 'string' && c.trim().length) {
			cachedToken = c.trim();
			return cachedToken;
		}
	}
	cachedToken = sessionToken;
	return cachedToken;
};

/**
 * Append `?v=<build-token>` (or merge with an existing query string)
 * so cached static assets are revalidated whenever the running build
 * version changes. Safe to call with relative `/foo/bar.wasm` paths
 * or absolute `https://…` URLs.
 */
export const withCacheBust = (url) => {
	if (typeof url !== 'string' || url.length === 0) return url;
	const token = computeCacheToken();
	const sep = url.includes('?') ? '&' : '?';
	return `${url}${sep}v=${encodeURIComponent(token)}`;
};

export const getBuildCacheToken = computeCacheToken;
